(() => {
  const NAMESPACE = 'CCCCommercePolish';
  const CUSTOM_DESIGN_OPTION = 'cookie-design-type';
  const CUSTOM_FORMAT_OPTION = 'format';
  const CUSTOM_DRAFT_TTL_MS = 5 * 60 * 1000;

  const normalizeOptionValue = (value) => String(value || '').trim().toLowerCase();
  const customFormatPolicy = Object.freeze({
    designOption: CUSTOM_DESIGN_OPTION,
    formatOption: CUSTOM_FORMAT_OPTION,
    isEligibleDesignType(value) {
      const normalized = normalizeOptionValue(value);
      return normalized.startsWith('embosser') || normalized.startsWith('cutter');
    },
    isDebosser(value) {
      return normalizeOptionValue(value).startsWith('debosser');
    },
    isPhysicalFormat(value) {
      return /^physical(?:\s+(?:product|item))?$/.test(normalizeOptionValue(value));
    },
    formatForDesignType(designType, selectedFormat, physicalFormat = 'Physical Product') {
      return this.isEligibleDesignType(designType) ? selectedFormat : physicalFormat;
    },
    resolveVariant(variants, selectedOptions) {
      if (!Array.isArray(variants) || !Array.isArray(selectedOptions)) return null;
      return variants.find((variant) =>
        Array.isArray(variant.options)
        && variant.options.length === selectedOptions.length
        && variant.options.every((value, index) => value === selectedOptions[index])
      ) || null;
    },
    hasSelectedUpload(inputs) {
      return [...(inputs || [])].some((input) => Number(input?.files?.length || 0) > 0);
    },
    confirmReloadWithUpload(inputs, confirmReload) {
      return !this.hasSelectedUpload(inputs) || Boolean(confirmReload());
    },
    draftStorageKey(productId) {
      return `${NAMESPACE}:custom-draft:${String(productId || '').trim()}`;
    },
  });

  const isVisible = (element) => {
    if (!element) return false;
    const style = window.getComputedStyle(element);
    const rect = element.getBoundingClientRect();
    return style.display !== 'none' && style.visibility !== 'hidden' && rect.width > 0 && rect.height > 0;
  };

  const initProduct = (root) => {
    if (!root || root.dataset.cccCommerceReady === 'true') return;
    root.dataset.cccCommerceReady = 'true';

    const sticky = root.querySelector('[data-ccc-mobile-buybar]');
    const submit = root.querySelector('[id^="ProductSubmitButton-"]');
    const info = root.querySelector('.product__info-wrapper');
    const stickyButton = sticky?.querySelector('[data-ccc-buybar-submit]');
    const stickyVariant = sticky?.querySelector('[data-ccc-buybar-variant]');
    const stickyPrice = sticky?.querySelector('[data-ccc-buybar-price]');
    const mobile = window.matchMedia('(max-width: 749px)');
    const variants = (() => {
      try {
        return JSON.parse(root.querySelector('[data-ccc-variants]')?.textContent || '[]');
      } catch {
        return [];
      }
    })();

    const pickerGroups = (picker) => [
      ...(picker?.querySelectorAll(':scope > [data-ccc-option-name]') || []),
    ];
    const selectedGroupValue = (group) => group?.querySelector('input[type="radio"]:checked')?.value
      || group?.querySelector('select')?.value
      || '';
    const controlsForGroup = (group) => [
      ...(group?.querySelectorAll('input[type="radio"], option') || []),
    ];
    const setFormatVisibility = (group, visible) => {
      group.hidden = !visible;
      if (visible) group.removeAttribute('aria-hidden');
      else group.setAttribute('aria-hidden', 'true');
    };
    const currentVariantId = () => root.querySelector('form[action*="/cart/add"] [name="id"]')?.value;
    const variantDestination = (variant) => {
      const destination = new URL(root.dataset.url, window.location.origin);
      destination.searchParams.set('variant', variant.id);
      return destination.toString();
    };
    let conditionalFormatRedirecting = false;

    const isCustomProduct = root.classList.contains('ccc-commerce-product--custom');
    const customDraftStorageKey = customFormatPolicy.draftStorageKey(root.dataset.productId);
    const customTextFieldEntries = () => {
      const occurrences = new Map();
      return [...root.querySelectorAll('input[type="text"], textarea')]
        .filter((field) => !field.disabled && (
          String(field.name || '').startsWith('properties[')
          || field.closest('.gpo-app, .globo-form-app, .gpo-element, .gpo-form__group, .globo-form-control')
        ))
        .map((field) => {
          const baseKey = field.name
            ? `name:${field.name}`
            : field.id
              ? `id:${field.id}`
              : '';
          if (!baseKey) return null;
          const occurrence = occurrences.get(baseKey) || 0;
          occurrences.set(baseKey, occurrence + 1);
          return { field, key: `${baseKey}:${occurrence}` };
        })
        .filter(Boolean);
    };
    const persistCustomDraft = () => {
      if (!isCustomProduct || !root.dataset.productId) return;
      const fields = customTextFieldEntries()
        .filter(({ field }) => String(field.value || '') !== '')
        .map(({ field, key }) => ({ key, value: field.value }));
      try {
        if (!fields.length) {
          window.sessionStorage.removeItem(customDraftStorageKey);
          return;
        }
        window.sessionStorage.setItem(customDraftStorageKey, JSON.stringify({
          productId: String(root.dataset.productId),
          expiresAt: Date.now() + CUSTOM_DRAFT_TTL_MS,
          fields,
        }));
      } catch {
        // A disabled storage API must never block a valid variant transition.
      }
    };
    const restoreCustomDraft = () => {
      if (!isCustomProduct || !root.dataset.productId) return;
      let draft;
      try {
        draft = JSON.parse(window.sessionStorage.getItem(customDraftStorageKey) || 'null');
      } catch {
        return;
      }
      if (
        !draft
        || draft.productId !== String(root.dataset.productId)
        || !Number.isFinite(draft.expiresAt)
        || draft.expiresAt < Date.now()
        || !Array.isArray(draft.fields)
      ) {
        try { window.sessionStorage.removeItem(customDraftStorageKey); } catch { /* no-op */ }
        return;
      }

      const fieldsByKey = new Map(customTextFieldEntries().map((entry) => [entry.key, entry.field]));
      const remaining = [];
      for (const saved of draft.fields) {
        const field = fieldsByKey.get(saved?.key);
        if (!field || typeof saved.value !== 'string') {
          remaining.push(saved);
          continue;
        }
        field.value = saved.value;
        field.dispatchEvent(new Event('input', { bubbles: true }));
        field.dispatchEvent(new Event('change', { bubbles: true }));
      }

      try {
        if (remaining.length) {
          window.sessionStorage.setItem(customDraftStorageKey, JSON.stringify({ ...draft, fields: remaining }));
        } else {
          window.sessionStorage.removeItem(customDraftStorageKey);
        }
      } catch {
        // The fields are already restored; storage cleanup is best effort.
      }
    };
    const prepareCustomReload = () => {
      if (!isCustomProduct) return true;
      if (
        !customFormatPolicy.confirmReloadWithUpload(
          root.querySelectorAll('input[type="file"]'),
          () => window.confirm(
            'Changing the format or design type reloads this page and clears your selected upload. '
            + 'Continue? Your text and notes will be kept.'
          )
        )
      ) return false;
      persistCustomDraft();
      return true;
    };
    const navigateToVariant = (variant, replace = false) => {
      if (!prepareCustomReload()) return false;
      if (replace) window.location.replace(variantDestination(variant));
      else window.location.assign(variantDestination(variant));
      return true;
    };

    const syncConditionalFormat = () => {
      if (!isCustomProduct) return;

      const picker = root.querySelector('variant-selects');
      const groups = pickerGroups(picker);
      const designGroup = groups.find((group) => group.dataset.cccOptionName === CUSTOM_DESIGN_OPTION);
      const formatGroup = groups.find((group) => group.dataset.cccOptionName === CUSTOM_FORMAT_OPTION);
      if (!designGroup || !formatGroup) return;

      const designType = selectedGroupValue(designGroup);
      const eligible = customFormatPolicy.isEligibleDesignType(designType);
      setFormatVisibility(formatGroup, eligible);
      formatGroup.dataset.cccConditionalFormat = 'true';
      if (eligible || conditionalFormatRedirecting) return;

      const selectedFormat = selectedGroupValue(formatGroup);
      if (customFormatPolicy.isPhysicalFormat(selectedFormat)) return;

      const physicalControl = controlsForGroup(formatGroup)
        .find((control) => customFormatPolicy.isPhysicalFormat(control.value));
      if (!physicalControl) return;

      const selectedOptions = groups.map(selectedGroupValue);
      selectedOptions[groups.indexOf(formatGroup)] = physicalControl.value;
      const physicalVariant = customFormatPolicy.resolveVariant(variants, selectedOptions);
      if (!physicalVariant || String(physicalVariant.id) === String(currentVariantId())) return;

      if (!prepareCustomReload()) return;
      conditionalFormatRedirecting = true;
      window.location.replace(variantDestination(physicalVariant));
    };

    let conditionalFormatFrame;
    const scheduleConditionalFormatSync = () => {
      window.cancelAnimationFrame(conditionalFormatFrame);
      conditionalFormatFrame = window.requestAnimationFrame(syncConditionalFormat);
    };

    /* Some option apps synchronously restore the original radio after a
       physical/digital format click. A full variant URL is the safest fallback:
       Shopify renders the chosen format server-side and every app reinitialises
       against the correct variant. Size/colour options keep Dawn's AJAX path. */
    root.addEventListener('click', (event) => {
      const label = event.target.closest?.('variant-selects label[for]');
      const input = event.target.matches?.('variant-selects input[type="radio"]')
        ? event.target
        : label && root.querySelector(`#${CSS.escape(label.htmlFor)}`);
      if (!input || input.checked || variants.length === 0) return;

      const picker = input.closest('variant-selects');
      const group = input.closest('fieldset, .product-form__input--dropdown');
      const groups = pickerGroups(picker);
      const optionIndex = groups.indexOf(group);
      if (optionIndex < 0) return;

      const selectedOptions = groups.map((optionGroup, index) => {
        if (index === optionIndex) return input.value;
        return optionGroup.querySelector('input[type="radio"]:checked')?.value
          || optionGroup.querySelector('select')?.value
          || '';
      });
      const currentId = currentVariantId();
      const currentVariant = variants.find((variant) => String(variant.id) === String(currentId));
      const designIndex = groups.findIndex((optionGroup) => optionGroup.dataset.cccOptionName === CUSTOM_DESIGN_OPTION);
      const formatIndex = groups.findIndex((optionGroup) => optionGroup.dataset.cccOptionName === CUSTOM_FORMAT_OPTION);
      const hasConditionalFormat = root.classList.contains('ccc-commerce-product--custom')
        && designIndex >= 0
        && formatIndex >= 0;

      if (hasConditionalFormat) {
        const nextDesignType = selectedOptions[designIndex];
        const formatGroup = groups[formatIndex];
        const eligible = customFormatPolicy.isEligibleDesignType(nextDesignType);
        setFormatVisibility(formatGroup, eligible);

        if (group.dataset.cccOptionName === CUSTOM_FORMAT_OPTION && !eligible) {
          event.preventDefault();
          event.stopImmediatePropagation();
          scheduleConditionalFormatSync();
          return;
        }

        if (group.dataset.cccOptionName === CUSTOM_DESIGN_OPTION && customFormatPolicy.isDebosser(nextDesignType)) {
          const physicalControl = controlsForGroup(formatGroup)
            .find((control) => customFormatPolicy.isPhysicalFormat(control.value));
          if (!physicalControl) {
            event.preventDefault();
            event.stopImmediatePropagation();
            return;
          }

          if (!customFormatPolicy.isPhysicalFormat(selectedOptions[formatIndex])) {
            selectedOptions[formatIndex] = customFormatPolicy.formatForDesignType(
              nextDesignType,
              selectedOptions[formatIndex],
              physicalControl.value
            );
            const physicalVariant = customFormatPolicy.resolveVariant(variants, selectedOptions);
            event.preventDefault();
            event.stopImmediatePropagation();
            if (physicalVariant) {
              if (!navigateToVariant(physicalVariant)) {
                setFormatVisibility(
                  formatGroup,
                  customFormatPolicy.isEligibleDesignType(selectedGroupValue(groups[designIndex]))
                );
              }
            } else {
              setFormatVisibility(formatGroup, customFormatPolicy.isEligibleDesignType(selectedGroupValue(groups[designIndex])));
            }
            return;
          }
        }
      }

      const exactVariant = customFormatPolicy.resolveVariant(variants, selectedOptions);
      const candidates = variants.filter((variant) => variant.options?.[optionIndex] === input.value);
      const isFormatSwitch = group.dataset.cccOptionName === CUSTOM_FORMAT_OPTION
        || /digital|stl|physical/i.test(input.value)
        || Boolean(
          exactVariant
          && currentVariant
          && exactVariant.requires_shipping !== currentVariant.requires_shipping
        );
      if (!isFormatSwitch) return;

      const variant = exactVariant || (hasConditionalFormat ? null : candidates.find((candidate) => candidate.available));
      if (!variant) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      navigateToVariant(variant);
    }, true);

    if (isCustomProduct) {
      root.addEventListener('change', scheduleConditionalFormatSync);
      new MutationObserver(() => {
        scheduleConditionalFormatSync();
        restoreCustomDraft();
      }).observe(root, {
        subtree: true,
        childList: true,
      });
      restoreCustomDraft();
      syncConditionalFormat();
    }

    const selectedVariantLabel = () => {
      const radioLabels = [...root.querySelectorAll('variant-selects input[type="radio"]:checked')]
        .map((input) => input.value?.trim())
        .filter(Boolean);
      const selectLabels = [...root.querySelectorAll('variant-selects select')]
        .map((select) => select.value?.trim())
        .filter(Boolean);
      const labels = [...radioLabels, ...selectLabels];
      return labels.join(' · ') || 'Selected option';
    };

    const visiblePrice = () => {
      const salePrice = [...root.querySelectorAll('.product__info-container .price-item--sale')].find(isVisible);
      const regularPrice = [...root.querySelectorAll('.product__info-container .price-item--regular')].find(isVisible);
      const price = salePrice || regularPrice;
      return price?.textContent.replace(/\s+/g, ' ').trim() || '';
    };

    const syncSticky = () => {
      if (!sticky || !stickyButton || !submit) return;
      const disabled = submit.disabled || submit.getAttribute('aria-disabled') === 'true';
      const submitText = submit.querySelector('span')?.textContent.replace(/\s+/g, ' ').trim();
      stickyButton.disabled = disabled;
      stickyButton.toggleAttribute('aria-disabled', disabled);
      stickyButton.setAttribute('aria-busy', submit.getAttribute('aria-busy') || 'false');
      stickyButton.querySelector('span').textContent = submitText || 'Add to cart';
      if (stickyVariant) stickyVariant.textContent = selectedVariantLabel();
      if (stickyPrice) stickyPrice.textContent = visiblePrice();
    };

    const updateStickyVisibility = () => {
      if (!sticky || !submit || !info) return;
      sticky.hidden = !mobile.matches;
      if (!mobile.matches) {
        sticky.classList.remove('is-visible');
        return;
      }

      const infoRect = info.getBoundingClientRect();
      const submitRect = submit.getBoundingClientRect();
      const rootRect = root.getBoundingClientRect();
      const purchaseButtonVisible = submitRect.top < window.innerHeight && submitRect.bottom > 0;
      const journeyHasStarted = infoRect.top <= 110;
      const productStillActive = rootRect.bottom > 100;
      sticky.classList.toggle('is-visible', journeyHasStarted && !purchaseButtonVisible && productStillActive);
    };

    if (sticky && submit && stickyButton) {
      sticky.hidden = !mobile.matches;
      stickyButton.addEventListener('click', () => {
        syncSticky();
        if (submit.disabled || submit.getAttribute('aria-disabled') === 'true') return;
        submit.click();
      });

      let frame;
      const schedule = () => {
        window.cancelAnimationFrame(frame);
        frame = window.requestAnimationFrame(() => {
          syncSticky();
          updateStickyVisibility();
        });
      };

      window.addEventListener('scroll', schedule, { passive: true });
      window.addEventListener('resize', schedule, { passive: true });
      mobile.addEventListener?.('change', schedule);
      root.addEventListener('change', () => window.setTimeout(schedule, 0));
      new MutationObserver(schedule).observe(root.querySelector('.product__info-container'), {
        subtree: true,
        childList: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['disabled', 'aria-disabled', 'class'],
      });

      document.addEventListener('focusin', (event) => {
        if (!sticky.classList.contains('is-visible') || sticky.contains(event.target)) return;
        if (event.target.matches?.(':focus-visible') || /^(INPUT|SELECT|TEXTAREA)$/.test(event.target.tagName)) {
          sticky.classList.add('is-focus-suppressed');
        }
      });
      document.addEventListener('pointerdown', () => sticky.classList.remove('is-focus-suppressed'), { passive: true });
      schedule();
    }

    if (root.classList.contains('ccc-commerce-product--custom')) {
      const errorSelectors = [
        '.gpo-error-message',
        '.gpo-message-error',
        '.gpo-error',
        '.gpo-element.has-error .help-block.error',
        '.gpo-element.has-error .messages .error',
        '.globo-form-app .message',
        '[aria-invalid="true"]',
      ].join(',');
      let validationRequested = false;

      const enhanceErrors = (focusFirst = false) => {
        const errors = [...root.querySelectorAll(errorSelectors)].filter(isVisible);
        let firstField = null;

        errors.forEach((error, index) => {
          if (error.matches('input, select, textarea')) {
            if (error.getAttribute('aria-invalid') !== 'true') error.setAttribute('aria-invalid', 'true');
            firstField ||= error;
            return;
          }

          if (!error.id) error.id = `CccOptionError-${root.dataset.section}-${index + 1}`;
          error.setAttribute('role', 'alert');
          error.setAttribute('aria-live', 'assertive');
          const group = error.closest('.gpo-element, .gpo-form__group, .globo-form-control') || error.parentElement;
          const field = group?.querySelector('input:not([type="hidden"]), select, textarea');
          if (field) {
            if (field.type === 'file' && /more than or equal to 1 files/i.test(error.textContent)) {
              error.textContent = 'Please upload at least one design or reference file.';
            } else if (field.name === 'Preferred size' && /field is required/i.test(error.textContent)) {
              error.textContent = 'Please choose a preferred size.';
            }
            if (field.getAttribute('aria-invalid') !== 'true') field.setAttribute('aria-invalid', 'true');
            const descriptions = new Set((field.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean));
            descriptions.add(error.id);
            field.setAttribute('aria-describedby', [...descriptions].join(' '));
            firstField ||= field;
          }
        });

        if (focusFirst && firstField) {
          firstField.focus({ preventScroll: false });
          validationRequested = false;
        }
      };

      root.addEventListener('click', (event) => {
        if (!event.target.closest('[id^="ProductSubmitButton-"], .gpo-clone-atc-button, [name="add"]')) return;
        validationRequested = true;
        window.setTimeout(() => enhanceErrors(true), 80);
        window.setTimeout(() => enhanceErrors(validationRequested), 350);
      }, true);

      root.addEventListener('input', (event) => {
        if (!event.target.matches('input, select, textarea')) return;
        event.target.removeAttribute('aria-invalid');
      });

      new MutationObserver(() => enhanceErrors(validationRequested)).observe(root, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['class', 'hidden', 'aria-invalid'],
      });
    }
  };

  const init = (scope = document) => {
    scope.querySelectorAll?.('product-info.ccc-commerce-product').forEach(initProduct);
  };

  if (window[NAMESPACE]) {
    window[NAMESPACE].customFormatPolicy = customFormatPolicy;
    window[NAMESPACE].init(document);
    return;
  }

  window[NAMESPACE] = { init, customFormatPolicy };
  init(document);
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
